import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "./StampDutyPage.module.scss";
import { generatePDFDocumentFromRef } from "../../../util/PDFRelated/generatePDF";
import { NavLink } from "react-router-dom";
import { getLang } from "../../../features/app/UserRelatedSlice";
import { useDispatch, useSelector } from "react-redux";
import { getIsRealMobile } from "../../../features/app/UIRelatedSlice";

/*
Test cases: All tested

A. UK buyer & first home
250000  0
300000  0
350000  2,500.00    0.7%
360000  3,000.00    0.8%
499999  9,999.95    2.0%
500000  10,000.00   2.0% 
500001  15,000.05   3.0%
800000  30,000.00   3.8%
925000  36,250.00   3.9%
1000000 43,750.00   4.4%
1500000 93,750.00   6.3%
2000000 153,750.00  7.7%

B. UK buyer & second home
39999   0                 
40000   2,000.00    5.0%
100000  5,000.00    5.0%
200000  11,500.00   5.8%
240000  14,300.00   6.0%
250000  15,000.00   6.0%
800000  70,000.00   8.8%
925000  82,500.00   8.9%
1000000 93,750.00   9.4%
1500000 168,750.00  11.3%
1600000 185,750.00  11.6%
2000000 253,750.00  12.7%

C. non UK buyer & first home
240000  4,800.00    2.0%
250000  5,000.00    2.0%
299999  5,999.98    2.0%
300000  6,000.00    2.0%
499999  19,999.93   4.0%
500000  20,000.00   4.0% 
500001  25,000.07   5.0%
800000  46,000.00   5.8%
925000  54,750.00   5.9%
1000000 63,750.00   6.4%
1500000 123,750.00  8.3%
1600000 137,750.00  8.6%
2000000 193,750.00  9.7%

D. non UK buyer & second home
39999   0           0.0% 
40000   2,800.00    7.0%
100000  7,000.00    7.0%
200000  15,500.00   7.8%
240000  19,100.00   8.0%
250000  20,000.00   8.0%
800000  86,000.00   10.8%
925000  101,000.00  10.9%
1000000 113,750.00  11.4%
1500000 198,750.00  13.3%
1600000 217,750.00  13.6%
2000000 293,750.00  14.7%


 */

export default function StampDutyPage(props: any) {
  const [isFirstHome, setIsFirstHome] = useState(true);
  const [propertyPrice, setPropertyPrice] = useState(0);
  const [propertyPriceDisplay, setPropertyPriceDisplay] = useState("0");
  const [isNonUKResident, setIsNonUKResident] = useState(false);
  const [taxTable, setTaxTable] = useState({ status: "idle", data: [] });
  const [taxData, setTaxData] = useState({
    status: "idle",
    data: { tax: 0, rate: 0 },
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const lang = useSelector(getLang);
  const isRealMobile = useSelector(getIsRealMobile);
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  function handleCalculate(e: any) {
    e.preventDefault();
    let bands = [
      { lower: 0, upper: 125000, rate: 0.0 },
      { lower: 125000, upper: 250000, rate: 0.02 },
      { lower: 250000, upper: 925000, rate: 0.05 },
      { lower: 925000, upper: 1500000, rate: 0.1 },
      { lower: 1500000, upper: Infinity, rate: 0.12 },
    ];

    // Surcharge rates
    const nonUKResidentSurcharge =
      isNonUKResident &&
      (isFirstHome || (!isFirstHome && +propertyPrice >= 40000))
        ? 0.02
        : 0;
    const additionalPropertySurcharge =
      !isFirstHome && +propertyPrice >= 40000 ? 0.05 : 0;

    // Adjust for first-time buyer exemption
    if (isFirstHome && +propertyPrice <= 500000) {
      bands[0].upper = 300000; // No SDLT on the first £300,000
      bands[1].lower = 300000;
      bands[2].lower = 300000; // 5% on the portion from £300,001 to £500,000
      bands.splice(1, 1);
    }

    if (!isFirstHome && +propertyPrice < 40000) {
      bands = [{ lower: 0, upper: 39999, rate: 0.0 }];
    }

    let tax = 0;
    let tableData: any = [];
    let price = +propertyPrice;

    bands.forEach((band) => {
      if (price > band.lower) {
        let effectiveRate =
          band.rate + nonUKResidentSurcharge + additionalPropertySurcharge;
        const taxableSum = Math.min(price, band.upper) - band.lower;
        const taxForBand = taxableSum * effectiveRate;
        tax += taxForBand;

        let taxBandText =
          band.upper === Infinity
            ? "£1,500,000+"
            : `£${band.lower.toLocaleString()} - £${band.upper.toLocaleString()}`;

        tableData.push({
          taxBand: taxBandText,
          rate: `${(effectiveRate * 100).toFixed(0)}%`,
          taxableSum: `£${taxableSum.toLocaleString()}`,
          tax: `£${taxForBand.toFixed(2)}`,
        });
      }
    });

    setTaxTable({ status: "success", data: tableData });
    setTaxData({
      status: "success",
      data: {
        rate: tax ? parseFloat(((tax / price) * 100).toFixed(1)) : 0,
        tax: +tax.toFixed(2),
      },
    });
  }

  const handleChange = (e: any) => {
    setIsFirstHome(e.target.value === "first");
  };

  function handlePropertyPriceInput(e: any) {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^0-9]/g, "");
    const numericValue = parseInt(inputValue, 10);

    if (!isNaN(numericValue)) {
      setPropertyPrice(numericValue);
      setPropertyPriceDisplay(numericValue.toLocaleString());
    } else {
      setPropertyPrice(0);
      setPropertyPriceDisplay("0");
    }
  }

  function handleDownloadPDF() {
    setIsGeneratingPDF(true);
  }

  useEffect(() => {
    (async () => {
      if (isGeneratingPDF && tableRef.current) {
        const doc = await generatePDFDocumentFromRef(
          tableRef.current,
          props.page === "tool"
        );
        if (doc) {
          if (!isRealMobile) {
            doc.save(`NVRE Stamp Duty Calculation.pdf`);
          } else {
            const pdfBlob = doc.output("blob");
            const link = document.createElement("a");
            link.href = URL.createObjectURL(pdfBlob);
            link.download = "NVRE Stamp Duty Calculation.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          setIsGeneratingPDF(false);
        }
      }
    })();
  }, [isGeneratingPDF]);

  return (
    <div
      className={`${styles.stampDutyPage} content-inner ${
        props.page !== "tool" && "p-6 sm:p-16 mb-[240px]"
      } font-main-sans max-w-[960px] mx-auto`}
    >
      <div className={` `}>
        <h1 id="service-title" className="font-semibold mb-8 text-4xl">
          {lang ? (
            <span>
              Stamp Duty Land Tax (SDLT)
              {props.page === "tool" ? (
                <span> 2025/2026 </span>
              ) : (
                <span>
                  <br />
                  Information and Calculator 2025/2026
                </span>
              )}
            </span>
          ) : (
            <span>
              印花税土地税 (SDLT)
              {props.page === "tool" ? (
                <span> 2025/2026</span>
              ) : (
                <span>
                  <br /> 信息和计算器 2025/2026
                </span>
              )}
            </span>
          )}
        </h1>
        {props.page === "tool" && (
          <NavLink
            target="_blank"
            rel="noopener noreferrer"
            className={"text-main/50  block -mt-6 mb-8 underline"}
            to="/services/stamp-duty-calculator"
          >
            {lang ? "Learn more" : "了解更多"}
          </NavLink>
        )}
        {props.page !== "tool" && (
          <p className="font-main-sans font-light text-base">
            {lang
              ? "Stamp Duty Land Tax (SDLT) is a tax paid on property or land purchases in England and Northern Ireland. The tax varies if the property is in Scotland (Land and Buildings Transaction Tax) or Wales (Land Transaction Tax). SDLT applies when you buy a freehold property, a new or existing leasehold, a property through a shared ownership scheme, or when land or property is transferred in exchange for payment."
              : "印花税土地税 (SDLT) 是在英格兰和北爱尔兰购买房产或土地时支付的税款。如果房产位于苏格兰（土地和建筑物交易税）或威尔士（土地交易税），税额会有所不同。当您购买不动产、新的或现有的租赁权、通过共有权方案购买的房产，或者在交换付款时转让土地或房产时，需要支付 SDLT。"}
          </p>
        )}
        <div
          ref={tableRef}
          id="to-print-stamp-duty"
          className={`mt-12 ${props.page === "tool" ? "tool" : ""}`}
        >
          <h2>{lang ? "Calculate your Stamp Duty" : "计算您的印花税"}</h2>
          <form
            id="margin-top"
            onSubmit={handleCalculate}
            className={`${styles.calculateForm} flex flex-col gap-3`}
            onChange={() => {
              setTaxData({ status: "idle", data: { tax: 0, rate: 0 } });
              setTaxTable({ status: "idle", data: [] });
            }}
          >
            <label className="hideSpinner flex items-center gap-3">
              <span
                className={`${
                  props.page === "tool" && "font-bold"
                } text-nowrap`}
              >
                {lang ? "Property Price" : "房产价格"}
              </span>
              <input
                className="input input-bordered  input-sm w-full max-w-xs bg-transparent"
                type="text"
                value={propertyPriceDisplay === "0" ? "" : propertyPriceDisplay}
                onChange={handlePropertyPriceInput}
                placeholder="500,000"
              />
            </label>
            <label className="flex items-center gap-3">
              <input
                className="checkbox "
                type="checkbox"
                checked={isNonUKResident}
                onChange={(e) => {
                  setIsNonUKResident(e.target.checked);
                }}
              />
              <span>
                {lang ? "The buyer is a non-UK resident" : "买家是非英国居民"}
              </span>
            </label>
            <div className="flex flex-col mt-3 sm:mt-0 sm:flex-row gap-3 sm:gap-6">
              <label className="flex items-center gap-3">
                <input
                  className="radio radio-sm"
                  type="radio"
                  value="first"
                  checked={isFirstHome}
                  onChange={handleChange}
                />
                <span>
                  {lang ? "Property is a first home" : "房产是第一套住房"}
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  className="radio radio-sm"
                  type="radio"
                  value="second"
                  checked={!isFirstHome}
                  onChange={handleChange}
                />
                <span>
                  {lang
                    ? "Property is a buy to let or second home"
                    : "房产是出租或第二套房"}
                </span>
              </label>
            </div>
            {!showResult(taxData, taxTable) && (
              <button
                className="btn btn-sm w-32 mt-6 bg-gray-100 text-gray-800"
                type="submit"
              >
                {lang ? "Calculate" : "计算"}
              </button>
            )}
          </form>
          {showResult(taxData, taxTable) && (
            <>
              <div className="w-full overflow-scroll">
                <h3>
                  <span className="text-main">{lang ? "Results" : "结果"}</span>
                </h3>
                <table className="table w-full">
                  <thead>
                    <tr className="text-base">
                      <th>{lang ? "Stamp Duty to pay:" : "应缴印花税："}</th>
                      <th>{lang ? "Effective Rate:" : "有效税率："}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-semibold text-xl text-secondary-dark opacity-80">
                      <td>£{taxData.data.tax.toLocaleString()}</td>
                      <td>{taxData.data.rate}%</td>
                    </tr>
                  </tbody>
                </table>
                <h3 className="text-3xl">
                  <span className="text-main">
                    {lang
                      ? "Stamp Duty Calculation Breakdown"
                      : "印花税计算细则"}
                  </span>
                </h3>
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th>{lang ? "Tax Band" : "税率区间"}</th>
                      <th>%</th>
                      <th>{lang ? "Taxable Sum" : "应税金额"}</th>
                      <th>{lang ? "Tax" : "税额"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxTable.data.map((row: any) => {
                      return (
                        <tr>
                          <td>{row.taxBand}</td>
                          <td>{row.rate}</td>
                          <td>{row.taxableSum}</td>
                          <td>{row.tax}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-20 mb-20 opacity-50">
                  <p>
                    {lang
                      ? "The amounts in the table are for reference only and are subject to actual occurrence;"
                      : "表中的金额以实际发生为准，以上数据仅供参考；"}
                  </p>
                  <p>
                    {lang
                      ? "If you reside continuously for 183 days after coming to the UK, you may be eligible for a 2% tax refund for overseas buyers with a visa that allows continuous residence."
                      : "来英后连续居住183天可以退2%海外买家税，但需要购房者有可以连续居住的签证。"}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        {showResult(taxData, taxTable) && (
          <div className="flex flex-col items-center">
            <button
              className="btn  btn-sm m-0 mt-0"
              onClick={handleDownloadPDF}
            >
              {isGeneratingPDF ? (
                <>{lang ? "Downloading PDF..." : "PDF 下载中..."}</>
              ) : (
                <>{lang ? "Download PDF" : "下载 PDF"}</>
              )}
            </button>
          </div>
        )}
      </div>
      {props.page !== "tool" && (
        <div className={`mt-36 text-gray-800 ${styles.aboutStampDuty}`}>
          <h2>{lang ? "About Stamp Duty Land Tax" : "印花税计算方式"}</h2>
          <h3>{lang ? "Stamp Duty Rates" : "印花税税率"}</h3>
          <p>{lang ? "Residential Properties" : "住宅物业"}</p>
          <table className="table text-gray-800">
            <tbody>
              <tr>
                <td>Up to £125,000</td>
                <td>0%</td>
              </tr>
              <tr>
                <td>£125,001 to £250,000</td>
                <td>2%</td>
              </tr>
              <tr>
                <td>£250,001 to £925,000</td>
                <td>5%</td>
              </tr>
              <tr>
                <td className="flex">
                  £925,001 to £1.5 {lang ? "million" : "百万"}
                </td>
                <td>10%</td>
              </tr>
              <tr>
                <td className="flex">Above £1.5 {lang ? "million" : "百万"}</td>
                <td>12%</td>
              </tr>
            </tbody>
          </table>

          <h4>{lang ? "First-Time Buyers" : "首次购房者"}</h4>
          <table className="table">
            <tbody>
              <tr>
                <td>Up to £300,000</td>
                <td>0%</td>
              </tr>
              <tr>
                <td>£300,001 to £500,000</td>
                <td>5%</td>
              </tr>
            </tbody>
          </table>
          <p>
            {lang
              ? "First-time buyer relief is not available for properties over £500,000"
              : "首次购房者减免不适用于超过 £500,000 的物业"}
          </p>

          <h4>{lang ? "Additional Properties" : "额外物业"}</h4>
          <p>
            {lang
              ? "An additional 5% on top of standard SDLT rates for buying new residential properties if you own more than one. Properties under £40,000 are not subject to the additional 5% tax."
              : "如果您拥有多于一处物业，购买新住宅物业的标准 SDLT 税率上将增加额外的 5%。价值 £40,000 以下的物业不需缴纳额外的 5% 税。"}
          </p>

          <h4>{lang ? "Non-UK Residents" : "非英国居民"}</h4>
          <p>
            {lang
              ? "A 2% surcharge for non-UK residents buying residential properties"
              : "非英国居民购买住宅物业需缴纳 2% 的附加费"}
          </p>

          <h3>{lang ? "SDLT Thresholds" : "SDLT 门槛"}</h3>
          <table className="table">
            <tbody>
              <tr>
                <td>
                  {lang ? "Standard Residential Properties:" : "标准住宅物业："}
                </td>
                <td>£125,000</td>
              </tr>
              <tr>
                <td>{lang ? "First-Time Buyers:" : "首次购房者："}</td>
                <td>
                  {lang
                    ? "£300,000 (for properties worth £500,000 or less)"
                    : "£300,000（适用于价值 £500,000 或以下的物业）"}
                </td>
              </tr>
            </tbody>
          </table>

          <h3>{lang ? "Eligibility and Reliefs" : "资格和减免"}</h3>
          <p>
            {lang
              ? "Various SDLT reliefs available for first-time buyers, multiple dwellings, and in specific situations like property transfers due to divorce or for charitable purposes."
              : "首次购房者、多套住宅以及特定情况（如因离婚或慈善目的的房产转让）可享受各种 SDLT 减免。"}
          </p>

          <h3>{lang ? "Calculation of SDLT" : "SDLT 的计算"}</h3>
          <p>
            {lang
              ? "The total value you pay SDLT on (the 'consideration') usually includes the price paid for the property or land and can sometimes include other types of payments like goods, works or services, or the release from a debt."
              : "您支付 SDLT 的总价值（'考虑因素'）通常包括为房产或土地支付的价格，有时还可能包括货物、工程或服务的其他类型支付，或债务的解除。"}
          </p>

          <h3>{lang ? "Payment and Deadlines" : "支付和截止日期"}</h3>
          <p>
            {lang
              ? "SDLT should be filed and paid within 14 days of completion of the property purchase."
              : "应在房产购买完成后 14 天内提交并支付 SDLT。"}
          </p>

          <h3>{lang ? "Exemptions" : "免税"}</h3>
          <p>
            {lang
              ? "Exemptions from SDLT or filing a return include property transfers with no payment exchange, inheritances, transfers due to divorce, properties under a certain value, and properties bought under alternative financial arrangements like Sharia law."
              : "免除 SDLT 或提交退货的情况包括无支付交换的财产转让、继承、离婚转让、某一价值以下的财产，以及根据伊斯兰教法等替代金融安排购买的财产。"}
          </p>

          <h3>{lang ? "Additional Information" : "附加信息"}</h3>
          <p>
            {lang
              ? "This calculator is for general interest and should not replace financial advice."
              : "此计算器仅供一般参考，不应替代财务咨询。"}
          </p>
          <p>
            {lang
              ? "Stamp duty can be added to mortgage loans, depending on the lender's terms."
              : "根据贷款人的条款，印花税可以加到抵押贷款上。"}
          </p>
        </div>
      )}
    </div>
  );
}

function showResult(taxData: any, taxTable: any) {
  return taxData.status === "success" && taxTable.status === "success";
}
